import React from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { locizePlugin } from 'locize'; // does things for direct edit ??
import LocizeBackend from 'i18next-locize-backend';
import i18n from 'i18next';

import { Settings } from 'luxon';
import { useSelector } from 'react-redux';
import { selectAppConfig } from '../../slices/appState';

const i18nConfig = {
  detection: {
    caches: ['cookie'],
    order: ['querystring', 'cookie', 'localStorage', 'navigator'],
    lookupQuerystring: 'culture',
  },
  compatibilityJSON: 'v3',
  interpolation: { prefix: '{{', suffix: '}}' },
  fallbackLng: 'de',
  ns: ['bookingjs'],
  load: 'languageOnly',
  backend: {
    projectId: 'ea068e6d-18b6-4697-bd21-65291883e27e',
    referenceLng: 'de',
  },
};

// Module-level init for backward compatibility (iframe paths get a fresh module per iframe)
const i18next = i18n
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  .use(locizePlugin)
  .use(LocizeBackend)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init(i18nConfig)
  .then(() => {
    Settings.defaultLocale = i18n.resolvedLanguage;
  })
  .then(async () => {
    i18n.fetchingLanguages =
      i18n.services.backendConnector.backend.getLanguages();

    i18n.loadedLanguages =
      await i18n.services.backendConnector.backend.getLanguages();
  });

/**
 * Creates a new isolated i18n instance for multi-instance usage.
 * Each call to init() should create its own i18n instance so that
 * language changes and resource bundles don't bleed between widgets.
 */
export function createI18nInstance() {
  const instance = i18n.createInstance();
  // locizePlugin is intentionally omitted here. It's only needed on the
  // module-level singleton for the "click to edit" dev feature. When a
  // child instance also registers it, the plugin's "save missing" tracker
  // injects empty-string markers for keys discovered via the parent page's
  // i18next instance (which shares the same locize project ID), polluting
  // the child's resource store with 25+ phantom empty keys. LocizeBackend
  // alone fetches the real translations cleanly.
  instance.readyPromise = instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .use(LocizeBackend)
    .init(i18nConfig)
    .then(() => {
      Settings.defaultLocale = instance.resolvedLanguage;
    })
    .then(async () => {
      instance.fetchingLanguages =
        instance.services.backendConnector.backend.getLanguages();

      instance.loadedLanguages =
        await instance.services.backendConnector.backend.getLanguages();
    });
  return instance;
}

export default function LanguageHandler() {
  const { i18n: i18nInstance } = useTranslation(undefined, {
    useSuspense: false,
  });
  const appConfig = useSelector(selectAppConfig);

  React.useEffect(() => {
    let active = true;
    setupLanguage();
    return () => {
      active = false;
    };

    async function setupLanguage() {
      if (appConfig) {
        // Wait for the i18n instance to be ready.
        // Per-instance i18n stores its promise on readyPromise;
        // the global i18n uses the module-level i18next promise.
        await (i18nInstance.readyPromise || i18next);

        if (appConfig.localization && active) {
          // Per-instance record of the overrides this handler has written,
          // mapping every applied key to the pristine (locize) value it shadows.
          // It lets a key the customer clears in the editor revert to its real
          // default instead of stranding the previous override — targeting only
          // the affected keys, so we never removeResourceBundle (which would
          // reopen the async-load race the empty-strip below closes).
          if (!i18nInstance.__bookingjsAppliedOverrides) {
            i18nInstance.__bookingjsAppliedOverrides = {};
          }
          const appliedByLang = i18nInstance.__bookingjsAppliedOverrides;

          for (const [lang, bundle] of Object.entries(appConfig.localization)) {
            // Drop empty-string overrides before merging. The AppConfigEditor
            // ships its full key set per language with '' for every un-filled
            // key; writing those '' into the store would shadow the real locize
            // translation. Since locize's async backfill merges with
            // overwrite=false, a shadowing '' can never be replaced afterwards,
            // leaving the string permanently blank (e.g. the book button and the
            // reservation countdown). This only surfaces in the core
            // BookingJsPreview, which re-applies live editor config — including
            // culture switches to not-yet-loaded languages — via repeated
            // 'rerender' postMessages; the live booking uses the saved config
            // (empties already stripped) and never hits this path.
            const overrides = Object.fromEntries(
              Object.entries(bundle).filter(
                ([, value]) =>
                  !(typeof value === 'string' && value.trim() === ''),
              ),
            );

            const prevApplied = appliedByLang[lang] ?? {};
            const nextApplied = {};
            const patch = {};
            // Snapshot the live bundle once; reads below see pristine values for
            // keys we have not overridden yet (an override we wrote on an earlier
            // render is recovered from prevApplied instead, never re-captured).
            const currentBundle = i18nInstance.getResourceBundle(
              lang,
              'bookingjs',
            );

            // Apply the current overrides, capturing each shadowed key's
            // pristine value the first time we override it.
            for (const [key, value] of Object.entries(overrides)) {
              nextApplied[key] =
                key in prevApplied ? prevApplied[key] : currentBundle?.[key];
              patch[key] = value;
            }

            // Revert keys overridden on an earlier render but now cleared: write
            // their captured pristine value back. Skip when no real value was
            // ever captured (e.g. the language had not loaded yet), which leaves
            // the store untouched rather than blanking the key.
            for (const key of Object.keys(prevApplied)) {
              if (!(key in overrides) && prevApplied[key] !== undefined) {
                patch[key] = prevApplied[key];
              }
            }

            appliedByLang[lang] = nextApplied;

            // Only touch the store when there is something to write. An
            // addResourceBundle call always materializes the bundle path, which
            // flips hasResourceBundle() to true and makes i18next skip the
            // locize fetch for that language — so writing an empty bundle for a
            // not-yet-loaded language would strand it on the fallback language.
            if (Object.keys(patch).length > 0) {
              // Deep-merge, overwrite=true: overrides win, reverted keys reset,
              // real keys are never removed, and there is no remove/re-add
              // window racing the async locize fetch.
              i18nInstance.addResourceBundle(
                lang,
                'bookingjs',
                patch,
                true,
                true,
              );
            }
          }
        }

        if (
          appConfig.culture &&
          appConfig.culture !== i18nInstance.resolvedLanguage &&
          active
        ) {
          await i18nInstance.changeLanguage(appConfig.culture);
          Settings.defaultLocale = i18nInstance.resolvedLanguage;
        }
      }
    }
  }, [appConfig, i18nInstance]);
}
